// Main-thread wrapper around the branch worker (src/sim/branchWorker.ts). One decision point and one
// alternative in, one alternate history out, with progress along the way.
//
// One branch at a time, like the wind tunnel's batch: starting a second cancels the first. The debrief
// has room for exactly one "what would have happened" pane, and two workers replaying two counter-
// factuals at once would only race each other to fill it.

import type { BranchOrder, BranchResult } from './branchRun';
import type { FromBranchWorker, ToBranchWorker } from './branchWorker';
import type { RulesOverrides, RulesPreset } from './rules';
import type { OrderLogEntry } from './sim';
import type { AoMeta, Dials, Scenario } from './types';

export type { BranchOrder, BranchResult };

export interface BranchClientArgs {
  meta: AoMeta;
  buffers: { cost: ArrayBuffer; cover: ArrayBuffer; elev: ArrayBuffer; roads: ArrayBuffer };
  scenario: Scenario;
  dials: Dials;
  seed: number;
  orderLog: OrderLogEntry[];
  branchTick: number;
  replacement: BranchOrder | null;
  extraOrders: BranchOrder[];
  untilTick: number;
  rulesPreset?: RulesPreset;
  rulesOverrides?: RulesOverrides;
}

export type BranchProgressHandler = (tick: number, untilTick: number) => void;

/** The one branch in flight, if any. */
let current: Worker | null = null;

/**
 * Runs one branch and resolves with its result.
 *
 * Rejects with `branch cancelled` if `cancel()` (or another `runBranchAsync`) interrupts it — the
 * caller treats that as "no answer", not as a failure worth showing the player.
 */
export function runBranchAsync(
  args: BranchClientArgs,
  onProgress?: BranchProgressHandler,
): Promise<BranchResult> {
  cancel();
  const worker = new Worker(new URL('./branchWorker.ts', import.meta.url), { type: 'module' });
  current = worker;

  return new Promise<BranchResult>((resolve, reject) => {
    const finish = (fn: () => void): void => {
      worker.terminate();
      if (current === worker) current = null;
      fn();
    };

    worker.onmessage = (e: MessageEvent<FromBranchWorker>): void => {
      const msg = e.data;
      switch (msg.type) {
        case 'progress':
          onProgress?.(msg.tick, msg.untilTick);
          break;
        case 'done': {
          const { type: _type, ...result } = msg;
          finish(() => resolve(result));
          break;
        }
        case 'error':
          finish(() => reject(new Error(msg.message)));
          break;
      }
    };
    worker.onerror = (e): void => finish(() => reject(new Error(e.message || 'branch worker failed')));

    // Structured-cloned, never transferred: the store keeps these buffers for the live worker and a
    // transferred ArrayBuffer would be detached out from under it (the same rule as batchClient).
    const msg: ToBranchWorker = { type: 'run', ...args };
    worker.postMessage(msg);

    (worker as Worker & { __wtCancel?: () => void }).__wtCancel = () =>
      finish(() => reject(new Error('branch cancelled')));
  });
}

/** Stops the branch in flight, if there is one. Safe to call when there is not. */
export function cancel(): void {
  const worker = current as (Worker & { __wtCancel?: () => void }) | null;
  current = null;
  worker?.__wtCancel?.();
}
